import React, { useState } from 'react';
import { Form, Input, Modal, Button } from 'antd';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { Container } from 'utils';
import config from 'config';
import { AlertActions } from 'redux/actions';
import EmailIcon from './Icons/EmailIcon';

const API_URL = config.REACT_APP_API_URL;

const NewsLetter = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [modalTooltip, setModalTooltip] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const apiUrl = `${API_URL}/brevo`;

    try {
      setSubmitting(true);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, phone }),
      });

      const data = response;

      if (response.ok && data?.brevo?.id != null) {
        dispatch(AlertActions.success('Vom Benutzer erstellt.'));
        form.resetFields();
        setEmail('');
        setPhone('');
        return;
      }

      dispatch(AlertActions.error('Der Benutzer wurde bereits erstellt.'));
    } catch (error) {
      dispatch(
        AlertActions.error(
          'Die E-Mail Adresse oder Telefonnummer wird bereits verwendet oder ist im falschen Format!'
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Section>
        <StyledContainer>
          <Content>
            <Left>
              <Heading>E-Mail und WhatsApp-Newsletter</Heading>

              <SupportingText>
                Erhalte aktuelle News über commonsplace bequem per Newsletter –
                melde dich jetzt an!
              </SupportingText>

              <HintText>
                Tritt auch jetzt unserem{' '}
                <a
                  href="https://whatsapp.com/channel/0029VaMMAq50QealT9ayL51r"
                  target="_blank"
                  rel="noreferrer"
                >
                  WhatsApp-Kanal
                </a>{' '}
                bei.
              </HintText>
            </Left>

            <Right>
              <StyledForm form={form} layout="vertical" onFinish={handleSubmit}>
                <FieldGroup>
                  <LabelRow>
                    <Label>E-Mail-Adresse</Label>
                    <Asterisk>*</Asterisk>
                  </LabelRow>

                  <Form.Item
                    name="email"
                    rules={[
                      {
                        required: true,
                        message: 'Deine E-Mail ist notwendig',
                      },
                      {
                        type: 'email',
                        message: 'Bitte gib eine gültige E-Mail-Adresse ein',
                      },
                    ]}
                    style={{ marginBottom: 0, width: '100%' }}
                  >
                    <EmailInputWrap>
                      <InputContent>
                        <MailIconWrap>
                          <EmailIcon />
                        </MailIconWrap>

                        <StyledInput
                          type="email"
                          placeholder="Deine E-Mail-Adresse"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </InputContent>
                    </EmailInputWrap>
                  </Form.Item>
                </FieldGroup>

                <PhoneFieldGroup>
                  <FieldGroup>
                    <LabelRow>
                      <Label>WhatsApp-Nummer</Label>
                    </LabelRow>

                    <Form.Item
                      name="phone"
                      style={{ marginBottom: 0, width: '100%' }}
                    >
                      <PhoneInputWrap>
                        <DropdownPart>
                          <DropdownText>+49</DropdownText>
                        </DropdownPart>

                        <PhoneTextInput
                          placeholder="(176) 000 000 00"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                        />
                      </PhoneInputWrap>
                    </Form.Item>
                  </FieldGroup>

                  <PhoneHintButton
                    type="button"
                    onClick={() => setModalTooltip(true)}
                  >
                    Erfahre mehr zu unseren Datenschutzrichtlinien.
                  </PhoneHintButton>
                </PhoneFieldGroup>

                <Actions>
                  <SubmitButton htmlType="submit" loading={submitting}>
                    Anmelden
                  </SubmitButton>
                </Actions>
              </StyledForm>
            </Right>
          </Content>
        </StyledContainer>
      </Section>

      <Modal
        title={null}
        centered
        open={modalTooltip}
        footer={null}
        onCancel={() => setModalTooltip(false)}
      >
        <ModalText>
          Es werden ggf. personenbezogene Daten an den Anbieter Sendinblue GmbH
          übermittelt und Cookies gesetzt.
        </ModalText>
      </Modal>
    </>
  );
};

export default NewsLetter;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 96px 0 64px;
  gap: 64px;
  width: 100%;
  background: #9dbc60;

  @media (max-width: 1024px) {
    padding: 72px 0 56px;
    gap: 40px;
  }

  @media (max-width: 768px) {
    padding: 56px 0 40px;
    gap: 32px;
  }
`;

const StyledContainer = styled(Container)`
  width: 100%;
  max-width: 1280px;
  padding: 0 32px;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  align-content: flex-start;
  gap: 32px;
  width: 100%;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  flex: 1;
  min-width: 480px;
  max-width: 768px;

  @media (max-width: 1024px) {
    min-width: 100%;
    max-width: 100%;
  }
`;

const Heading = styled.h2`
  width: 100%;
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 30px;
  line-height: 38px;
  color: #ffffff;

  @media (max-width: 768px) {
    font-size: 26px;
    line-height: 34px;
  }
`;

const SupportingText = styled.p`
  width: 100%;
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  line-height: 30px;
  color: #ffffff;

  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 28px;
  }
`;

const HintText = styled.p`
  width: 100%;
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #e6f4d7;

  a {
    color: inherit;
    text-decoration: underline;
  }
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
  width: 480px;

  @media (max-width: 1024px) {
    width: 100%;
    align-items: stretch;
  }
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
  gap: 16px;
  width: 100%;

  .ant-form-item {
    width: 100%;
    margin-bottom: 0;
  }

  .ant-form-item-explain-error {
    margin-top: 6px;
    color: #fff !important;
    font-family: 'Inter', sans-serif;
    font-size: 13px;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  width: 100%;
`;

const PhoneFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  width: 100%;
`;

const LabelRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 2px;
`;

const Label = styled.span`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #e6f4d7;
`;

const Asterisk = styled.span`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #9dbc60;
`;

const EmailInputWrap = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 12px 14px;
  gap: 8px;
  width: 100%;
  height: 48px;
  background: #ffffff;
  border: 1px solid #d5d7da;
  box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05);
  border-radius: 8px;
`;

const InputContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 24px;
`;

const MailIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #717680;
  flex: 0 0 auto;
`;

const StyledInput = styled(Input)`
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  height: 24px !important;
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #414651;

  &::placeholder {
    color: #717680;
  }
`;

const PhoneInputWrap = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
  height: 44px;
  background: #ffffff;
  border: 1px solid #d5d7da;
  box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05);
  border-radius: 8px;
  overflow: hidden;
`;

const DropdownPart = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0 10px 14px;
  gap: 4px;
  width: 56px;
  height: 44px;
  color: #414651;
  flex: 0 0 auto;
`;

const DropdownText = styled.span`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #414651;
`;

const PhoneTextInput = styled(Input)`
  border: none !important;
  box-shadow: none !important;
  padding: 10px 14px 10px 12px !important;
  height: 44px !important;
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #414651;

  &::placeholder {
    color: #717680;
  }
`;

const PhoneHintButton = styled.button`
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #e6f4d7;

  &:hover {
    text-decoration: underline;
  }
`;

const Actions = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

const SubmitButton = styled(Button)`
  &.ant-btn {
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 12px 18px;
    gap: 6px;
    width: 119px;
    height: 48px;
    background: #ffffff;
    border: 1px solid #acdc79;
    box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05),
      inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18),
      inset 0px -2px 0px rgba(10, 13, 18, 0.05);
    border-radius: 8px;
    font-family: 'Inter', sans-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    color: #3f621a;
  }

  &.ant-btn:hover,
  &.ant-btn:focus {
    background: #f5fbee !important;
    border-color: #acdc79 !important;
    color: #3f621a !important;
  }
`;

const ModalText = styled.p`
  margin-top: 25px;
  letter-spacing: 0.03em;
  font-size: 15px;
  line-height: 1.45;
  color: #304b50;
  font-weight: 400;
`;
